import { useState } from "react";
import {
  IconChevronDown,
  IconChevronUp,
  IconFlag3Filled,
  IconStarFilled,
} from "@tabler/icons-react";
import { SectionHeader } from "@/components/ui/section-header";
import { ParamComparisonTable } from "@/components/param-comparison-table";
import { groupAction } from "@/components/calibration-live-controls";
import type { WriteControls } from "@/components/calibration-live-controls";
import { useCalibrationHistory } from "@/api/hooks/use-calibrations";
import { PARAM_GROUPS, toParamMap, valuesMatch } from "@/domain/calibration-params";
import type { NvpParameter } from "@/domain/calibration-params";
import { goldenColumnLabel } from "@/domain/calibration-live";
import type { BuildId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type HistoryEntry = components["schemas"]["CalibrationHistoryEntry"];
type Calibration = components["schemas"]["CalibrationResponse"];

interface ComparisonsSectionProps {
  buildId: BuildId;
  currentGolden?: Calibration;
  initialParams?: NvpParameter[];
  eventName?: string;
  controls?: WriteControls;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function countDiffs(
  baseMap: Map<string, NvpParameter>,
  compareMap: Map<string, NvpParameter>,
): number {
  let count = 0;
  for (const group of PARAM_GROUPS) {
    for (const name of group.params) {
      const a = baseMap.get(name);
      const b = compareMap.get(name);
      const accuracy = b?.Accuracy ?? a?.Accuracy;
      const chpDataType = b?.ChpDataType ?? a?.ChpDataType;
      if (!valuesMatch(a?.Value, b?.Value, accuracy, chpDataType)) count++;
    }
  }
  return count;
}

function ComparisonRow({
  icon,
  label,
  meta,
  diffCount,
  hasCurrentGolden,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  meta?: React.ReactNode;
  diffCount?: number;
  hasCurrentGolden: boolean;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`
          flex items-center w-full bg-card border-l-3 border-l-border
          pl-4 pr-5 h-11 transition-shadow hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]
          ${expanded ? "" : "rounded-r-lg"}
        `}
      >
        <div className="flex items-center flex-1 min-w-0 gap-3">
          <span className="inline-flex items-center gap-1 text-sm font-medium">
            {icon}
            {label}
          </span>
          {meta}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {hasCurrentGolden && diffCount != null && (
            <>
              {diffCount > 0 ? (
                <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                  {diffCount} changed
                </span>
              ) : (
                <span className="text-xs font-medium text-green-600 dark:text-green-400">
                  Identical
                </span>
              )}
            </>
          )}
          {expanded ? (
            <IconChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <IconChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="bg-card border-l-3 border-l-border rounded-br-lg pl-4 pr-5 py-3 overflow-x-auto">
          {children}
        </div>
      )}
    </div>
  );
}

function HistoryRow({
  entry,
  currentParamMap,
  currentLabel,
  hasCurrentGolden,
  controls,
}: {
  entry: HistoryEntry;
  currentParamMap: Map<string, NvpParameter>;
  currentLabel: string;
  hasCurrentGolden: boolean;
  controls?: WriteControls;
}) {
  const historyParamMap = toParamMap(entry.parameters);

  return (
    <ComparisonRow
      icon={<IconStarFilled className="h-3 w-3 text-amber-500/70" />}
      label={entry.event_name}
      meta={
        <>
          <span className="text-xs text-muted-foreground">
            {formatDate(entry.event_date)}
          </span>
          <span className="text-xs text-muted-foreground/50">
            {entry.param_count} params
          </span>
        </>
      }
      diffCount={entry.diff_count}
      hasCurrentGolden={hasCurrentGolden}
    >
      <ParamComparisonTable
        baseParams={historyParamMap}
        baseLabel={entry.event_name}
        compareParams={hasCurrentGolden ? currentParamMap : undefined}
        compareLabel={hasCurrentGolden ? currentLabel : undefined}
        liveParams={controls?.live}
        groupActions={
          controls
            ? (group) => ({
                base: groupAction(
                  controls,
                  group,
                  { id: entry.id, label: entry.event_name },
                  historyParamMap,
                ),
                compare: hasCurrentGolden
                  ? groupAction(controls, group, controls.golden, currentParamMap)
                  : undefined,
              })
            : undefined
        }
      />
    </ComparisonRow>
  );
}

function InitialRow({
  initialParamMap,
  currentParamMap,
  currentLabel,
  controls,
}: {
  initialParamMap: Map<string, NvpParameter>;
  currentParamMap: Map<string, NvpParameter>;
  currentLabel: string;
  controls?: WriteControls;
}) {
  const diffCount = countDiffs(initialParamMap, currentParamMap);

  return (
    <ComparisonRow
      icon={<IconFlag3Filled className="h-3 w-3 text-blue-500" />}
      label="Initial"
      diffCount={diffCount}
      hasCurrentGolden={true}
    >
      <ParamComparisonTable
        baseParams={initialParamMap}
        compareParams={currentParamMap}
        baseLabel="Initial"
        compareLabel={currentLabel}
        liveParams={controls?.live}
        groupActions={
          controls
            ? // An initial is an as-found readout, never a write source.
              (group) => ({ compare: groupAction(controls, group, controls.golden, currentParamMap) })
            : undefined
        }
      />
    </ComparisonRow>
  );
}

export function ComparisonsSection({
  buildId,
  currentGolden,
  initialParams,
  eventName,
  controls,
}: ComparisonsSectionProps) {
  const { data: history } = useCalibrationHistory(buildId);

  const hasCurrentGolden = !!currentGolden?.parameters.length;
  const currentLabel = goldenColumnLabel(eventName);
  const currentParamMap = toParamMap(currentGolden?.parameters ?? []);

  const hasInitialComparison =
    hasCurrentGolden && !!initialParams?.length;
  const initialParamMap = initialParams
    ? new Map(initialParams.map((p) => [p.Name, p]))
    : null;

  const hasHistory = !!history?.length;

  if (!hasInitialComparison && !hasHistory) return null;

  return (
    <div className="mt-8">
      <SectionHeader>Comparisons</SectionHeader>
      <div className="space-y-1.5">
        {hasInitialComparison && initialParamMap && (
          <InitialRow
            initialParamMap={initialParamMap}
            currentParamMap={currentParamMap}
            currentLabel={currentLabel}
            controls={controls}
          />
        )}
        {history?.map((entry) => (
          <HistoryRow
            key={entry.id}
            entry={entry}
            currentParamMap={currentParamMap}
            currentLabel={currentLabel}
            hasCurrentGolden={hasCurrentGolden}
            controls={controls}
          />
        ))}
      </div>
    </div>
  );
}
